import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {CpmCOO8ConnectorApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-oo8-connector.api-service';
import {CpmCOO8ConnectorApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-oo8-connector.api-model';
import {
    CpmCOO8ConnectorCreateApiSchema,
    CpmCOO8ConnectorUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-global/cpmc-oo8-connector.api-schemas';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

export class NewEditOO8ConnectorModalConfig {
    constructor(public existing?: CpmCOO8ConnectorApiModel) {}
}
export class NewEditOO8ConnectorModalResult {
    constructor(public template?: CpmCOO8ConnectorApiModel) {}
}
/**
 * New/Edit OO8Connector modal
 */
@Component({
    selector: 'app-new-edit-oo8-connector-modal-modal',
    templateUrl: './new-edit-oo8-connector-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditOO8ConnectorModalComponent extends IBaseCRUModalComponent<
    CpmCOO8ConnectorApiModel,
    NewEditOO8ConnectorModalComponent,
    NewEditOO8ConnectorModalConfig,
    NewEditOO8ConnectorModalResult
> {
    protected item!: CpmCOO8ConnectorApiModel;
    protected _booleanOptions = [
        {
            label: 'Yes: Ensure certificates validity',
            value: true
        },
        {
            label: 'No: Ignore invalid certificates',
            value: false
        }
    ];

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('OO8ConnectorApiModel', (model: CpmCOO8ConnectorApiModel) => model.name);
        this.config.width = '40rem';
        this.contentMinHeight = '35rem';
        this.modelHumanName = 'OO8 Connector';

        this.updateModalTitle();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            name: new FormControl<string | null>(null, [Validators.required]),
            openid_connect_url: new FormControl<string | null>(null, [Validators.required]),
            frontend_url: new FormControl<string | null>(null, [Validators.required]),
            backend_url: new FormControl<string | null>(null, [Validators.required]),
            client_id: new FormControl<string | null>(null, [Validators.required]),
            client_secret: new FormControl<string | null>(null, [Validators.required]),
            verify_ssl: new FormControl<boolean | null>(null, [Validators.required])
        });

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the OO8 connector...';
            this.item = await firstValueFrom(
                CpmCOO8ConnectorApiService.getOO8ConnectorByUUID(this.config.data?.extended?.existing?.uuid ?? '')
            );

            this.form.patchValue(this.item);

            if (!this.isCreationMode) {
                this.form.controls['frontend_url'].disable();
                this.form.controls['backend_url'].disable();
                this.form.controls['verify_ssl'].disable();
            }
        } else {
            this.form.controls['openid_connect_url'].setValue(
                'https://__HOST__/realms/__REALM__/protocol/openid-connect/token'
            );
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCOO8ConnectorCreateApiSchema | CpmCOO8ConnectorUpdateApiSchema = this.isCreationMode
            ? {
                  name: this.form.controls['name']?.value,
                  openid_connect_url: this.form.controls['openid_connect_url']?.value,
                  frontend_url: this.form.controls['frontend_url']?.value,
                  backend_url: this.form.controls['backend_url']?.value,
                  client_id: this.form.controls['client_id']?.value,
                  client_secret: this.form.controls['client_secret']?.value,
                  verify_ssl: this.form.controls['verify_ssl']?.value
              }
            : {
                  name: this.form.controls['name']?.value,
                  client_id: this.form.controls['client_id']?.value,
                  client_secret: this.form.controls['client_secret']?.value,
                  verify_ssl: this.form.controls['verify_ssl']?.value
              };

        await this.submitCU(
            null,
            CpmCOO8ConnectorApiService.createOO8Connector,
            CpmCOO8ConnectorApiService.updateOO8Connector,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCOO8ConnectorApiModel) => new NewEditOO8ConnectorModalResult(resultCRUD)
        );
    }
}
